// src/components/GoldChart.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';
import api from '../api';

const tabs = [
  { id: 'daily', label: 'روزانه' },
  { id: 'weekly', label: 'هفتگی' },
  { id: 'monthly', label: 'ماهانه' }
];

const gradientColors = {
  stroke: '#6366f1',     // indigo-500
  fill: '#8b5cf6',       // purple-500
  dot: '#10b981',        // emerald-500
  up: '#10b981',
  down: '#ef4444'
};

export default function GoldChart({ height = 400 }) {
  const [activeTab, setActiveTab] = useState('daily');
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async (range) => {
    setIsLoading(true);
    try {
      const res = await api.get(`/api/set-price/chart-data?range=${range}`);
      
      if (!res?.data?.data || !Array.isArray(res.data.data)) {
        setChartData([]);
        setIsLoading(false);
        return;
      }
  
      const formattedData = res.data.data.map(item => ({
        price: item.price,
        time: item.time,
        fullTime: item.fullTime || 'نامشخص'
      }));
  
      setChartData(formattedData);
    } catch (err) {
      console.error('خطا در دریافت داده نمودار:', err);
      setChartData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(activeTab);
  }, [activeTab]);

  // محاسبه کف و سقف قیمت + پدینگ
  const { minPrice, maxPrice, priceChange, isPositive } = useMemo(() => {
    if (chartData.length === 0) return { minPrice: 0, maxPrice: 0, priceChange: 0, isPositive: true };

    const prices = chartData.map(d => d.price);
    const minRaw = Math.min(...prices);
    const maxRaw = Math.max(...prices);
    const diff = maxRaw - minRaw;

    // حداقل دامنه برای جلوگیری از خط صاف
    const minDiff = 100000; // حداقل ۱۰۰,۰۰۰ ریال
    const padding = Math.max(diff * 0.08, minDiff); // ۸٪ یا حداقل ۱۰۰k

    const minPrice = Math.floor((minRaw - padding) / 10000) * 10000;
    const maxPrice = Math.ceil((maxRaw + padding) / 10000) * 10000;

    const firstPrice = chartData[0].price;
    const lastPrice = chartData[chartData.length - 1].price;
    const change = ((lastPrice - firstPrice) / firstPrice) * 100;

    return {
      minPrice,
      maxPrice,
      priceChange: change,
      isPositive: change >= 0
    };
  }, [chartData]);

  const formatPrice = (v) => v?.toLocaleString('fa-IR') || '—';
  const formatPercent = (v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;

  // Tooltip سفارشی
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload[0]) {
      const data = payload[0].payload;
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="bg-white/95 backdrop-blur-sm p-4 rounded-2xl shadow-2xl border border-purple-200"
        >
          <p className="text-xs font-medium text-purple-600 mb-1">{data.fullTime}</p>
          <p className="text-lg font-bold text-indigo-700">
            {formatPrice(data.price)} ریال
          </p>
        </motion.div>
      );
    }
    return null;
  };

  // میانگین قیمت
  const averagePrice = chartData.length > 0
    ? Math.round(chartData.reduce((a, b) => a + b.price, 0) / chartData.length / 10000) * 10000
    : 0;

  return (
    <div className="bg-gradient-to-br from-purple-50 via-indigo-50 to-emerald-50 rounded-3xl shadow-xl p-6 border border-purple-100">
      
      {/* هدر: تب‌ها + وضعیت تغییر */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
        <div className="flex gap-2 flex-wrap">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all transform hover:scale-105 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                  : 'bg-white/80 text-gray-700 hover:bg-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          {/* تغییر قیمت */}
          {chartData.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm ${
                isPositive 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              <span>{formatPercent(priceChange)}</span>
            </motion.div>
          )}

          <button
            onClick={() => fetchData(activeTab)}
            disabled={isLoading}
            className="p-2.5 rounded-xl bg-white/80 hover:bg-white shadow-md transition-all disabled:opacity-50"
            title="رفرش دستی"
          >
            <RefreshCw className={`w-5 h-5 text-indigo-600 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* نمودار */}
      <div style={{ height }}>
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full"
            />
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="4 4" stroke="#e0e7ff" opacity={0.5} />
              
              <XAxis 
                dataKey="time" 
                tick={{ fontSize: 12, fill: '#6366f1' }}
                tickLine={false}
                axisLine={{ stroke: '#e0e7ff' }}
              />

              <YAxis 
                domain={[minPrice, maxPrice]}
                tick={{ fontSize: 12, fill: '#6366f1' }}
                tickLine={false}
                tickFormatter={formatPrice}
                axisLine={{ stroke: '#e0e7ff' }}
                padding={{ top: 20, bottom: 20 }}
              />

              <Tooltip 
                content={<CustomTooltip />}
                cursor={{ stroke: '#8b5cf6', strokeWidth: 2, strokeDasharray: '5 5' }}
              />

              {/* خط میانگین */}
              <ReferenceLine 
                y={averagePrice} 
                stroke="#6366f1" 
                strokeDasharray="6 6"
                label={{
                  value: `میانگین: ${formatPrice(averagePrice)}`,
                  position: 'insideTopRight',
                  fill: '#6366f1',
                  fontSize: 11,
                  fontWeight: 'bold'
                }}
              />

              <Line
                type="monotone"
                dataKey="price"
                stroke={gradientColors.stroke}
                strokeWidth={3}
                dot={{ fill: gradientColors.dot, r: 5, strokeWidth: 2, stroke: '#fff' }}
                activeDot={{ r: 8, fill: isPositive ? gradientColors.up : gradientColors.down, stroke: '#fff', strokeWidth: 3 }}
                animationDuration={1500}
                animationEasing="ease-out"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <div className="w-24 h-24 bg-gray-200 border-2 border-dashed rounded-xl mb-4" />
            <p className="text-lg font-medium">داده‌ای برای نمایش وجود ندارد</p>
            <p className="text-sm mt-1">لطفاً بعداً تلاش کنید</p>
          </div>
        )}
      </div>

      {/* فوتر: کف و سقف */}
      {chartData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mt-4 text-xs font-medium text-gray-600"
        >
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span>کف: {formatPrice(minPrice)}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
            <span>سقف: {formatPrice(maxPrice)}</span>
          </div>
        </motion.div>
      )}
    </div>
  );
}